import { Box, Button, Divider } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { endOfWeek, startOfWeek } from 'date-fns'

import { DateNavigator } from 'modules/core/components/DateNavigator'
import Filter from 'modules/core/components/Filter'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import AddProjectHint from 'modules/project_management/components/hints/myProjects/AddProjectHint'
import NavigateThroughWeeksHint from 'modules/project_management/components/hints/myProjects/NavigateThroughWeeksHint'

type Range = [Date, Date]
type FilterOption = {
  label: string
  value: string
}

interface IHeaderActionsProps {
  buildWeekNavigation: (week: Date) => void
  canRegisterFlexibleTracking: boolean
  filterValues: string[]
  hasGlobalTimeRecords: boolean
  range: Range
  setFilterValues: (values: string[]) => void
  setIsAddProjectModalOpen: (isOpen: boolean) => void
  setRange: (range: Range) => void
}

const HeaderActions = ({
  buildWeekNavigation,
  canRegisterFlexibleTracking,
  filterValues,
  hasGlobalTimeRecords,
  range,
  setFilterValues,
  setIsAddProjectModalOpen,
  setRange,
}: IHeaderActionsProps) => {
  const [localStorageFilters, setLocalStorageFilters] = useLocalStorage<string[]>(
    LOCAL_STORAGE_KEYS.myProjectsFilters,
    filterValues
  )
  const weekEnd = endOfWeek(new Date())
  const weekStart = startOfWeek(new Date())

  const favouriteProjectOption = {
    label: i18n.t('project_management.my_projects.table.filter_options.favourite_projects'),
    value: 'favourite_projects',
  }
  const lastWeekProjectsOption = {
    label: i18n.t('project_management.my_projects.table.filter_options.last_week_projects'),
    value: 'last_week_projects',
  }

  const availableOptions = [favouriteProjectOption, lastWeekProjectsOption]

  const selectedOptions = () => {
    const toggledOptions: FilterOption[] = []
    availableOptions.forEach((option) => {
      if (localStorageFilters?.includes(option.value)) {
        toggledOptions.push(option)
      }
    })
    return toggledOptions
  }

  const onClickToday = () => {
    buildWeekNavigation(weekStart)
    setRange([weekStart, weekEnd])
  }

  const setRangeDetails = (newRange: Range) => {
    buildWeekNavigation(newRange[0])
    setRange(newRange)
  }

  const handleFilterChange = (values: string[] | undefined) => {
    setFilterValues(values || [])
    setLocalStorageFilters(values || [])
    track(trackingEvents.projectManagement.flexibleTracking.filterSelected, {
      filter_type: 'show',
      filter_values: values,
    })
  }

  return (
    <>
      <Box
        paddingBottom="s16"
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
      >
        <Box paddingRight="s4" flexDirection="row">
          <Button type="secondary" onClick={onClickToday}>
            {i18n.t('common.today')}
          </Button>
          <Box paddingX="s8">
            <DateNavigator
              regularPeriods={['week']}
              startDate={range[0]}
              endDate={range[1]}
              onDateRangeChange={(...newRange) => setRangeDetails(newRange)}
            />
          </Box>
          <Divider vertical />
          <Box paddingLeft="s8">
            <Filter.MultipleChoice
              fullWidth
              hideFooter
              hideSearch
              label={i18n.t('common.show')}
              onValueChange={(values) => {
                handleFilterChange(values)
              }}
              options={availableOptions}
              selectedOptions={selectedOptions()}
              value={filterValues}
            />
          </Box>
        </Box>

        {canRegisterFlexibleTracking && (
          <AddProjectHint stepNumber={1} position="bottom left">
            <Button type="secondary" onClick={() => setIsAddProjectModalOpen(true)} iconName="add">
              {i18n.t('project_management.my_projects.table.empty_table_message.add_button')}
            </Button>
          </AddProjectHint>
        )}
      </Box>
      <NavigateThroughWeeksHint stepNumber={hasGlobalTimeRecords ? 4 : 3} />
    </>
  )
}

export default HeaderActions
